import { View } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { Progress } from "@-/dev.improve.haniverse.components.ui.progress";
import { Surface, SurfaceCollapsibleHeader } from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { getLevelsListEntryLabel } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._components.levels-list";
import { useLevelsPackSurfaceExpanded } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._components.pinyin-sound-charts";
import { BrowseLevelsLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._context";
import { useLevelsPackProgress } from "#use-levels-pack-progress";

/**
 * The pack sheet's body for every pack after Ready: how many of the pack's levels the learner has
 * completed and which comes next, then how many characters of each exam level or chart the pack
 * covers they have studied (HSK 2, HSK 3 and HSK 4 for Traveler). Its header's chevron collapses it
 * to the header, as on Ready's charts.
 */
export function LevelsPackProgressSurface() {
  const [levels] = useContextSelectors(BrowseLevelsLayoutContext, (c) => c.characterLevels);
  const { isPending, progress } = useLevelsPackProgress();
  const [isExpanded, setIsExpanded] = useLevelsPackSurfaceExpanded("progress");
  const nextLevel = progress.nextLevelIndex === null ? undefined : levels[progress.nextLevelIndex];
  const nextLevelLabel =
    nextLevel === undefined
      ? "Pack complete"
      : `Next: ${getLevelsListEntryLabel({ index: nextLevel.index, level: nextLevel })}`;

  return (
    <Surface testID="levels-pack-progress">
      <SurfaceCollapsibleHeader
        accessibilityLabel={isPending ? "Your progress" : `Your progress, ${nextLevelLabel}`}
        className="flex-row items-baseline justify-between gap-2 py-1.5"
        isExpanded={isExpanded}
        onExpandedChange={setIsExpanded}
        testID="levels-pack-progress-header"
      >
        <Text className="text-sm font-semibold">Your progress</Text>
        {isPending ? null : (
          <Text
            className="min-w-0 shrink text-xs text-muted-foreground"
            numberOfLines={1}
            testID="levels-pack-progress-next"
          >
            {nextLevelLabel}
          </Text>
        )}
      </SurfaceCollapsibleHeader>
      {isExpanded ? (
        <View className="gap-2 px-3 pt-2 pb-2.5">
          <LevelsPackProgressRow
            isPending={isPending}
            label="Levels"
            learned={progress.completedLevelCount}
            noun="levels completed"
            testID="levels-pack-progress-levels"
            total={progress.levelCount}
          />
          {progress.grades.length === 0 ? null : (
            <Text className="text-xs font-medium text-muted-foreground">Characters learned</Text>
          )}
          {progress.grades.map((grade) => (
            <LevelsPackProgressRow
              isPending={isPending}
              key={grade.grade}
              label={grade.label}
              learned={grade.learned}
              noun="characters learned"
              testID={`levels-pack-progress-grade-${grade.grade}`}
              total={grade.total}
            />
          ))}
        </View>
      ) : null}
    </Surface>
  );
}

function LevelsPackProgressRow({
  isPending,
  label,
  learned,
  noun,
  testID,
  total,
}: {
  isPending: boolean;
  label: string;
  learned: number;
  /** What `learned` counts, for screen readers ("levels completed"). */
  noun: string;
  testID: string;
  total: number;
}) {
  return (
    <View
      accessibilityLabel={
        isPending ? `${label}: loading` : `${label}: ${learned} of ${total} ${noun}`
      }
      accessible
      className="flex-row items-center gap-3"
      testID={testID}
    >
      <Text className="w-16 text-xs font-semibold" numberOfLines={1}>
        {label}
      </Text>
      <Progress
        className="h-1.5 flex-1 rounded-full bg-muted"
        indicatorClassName="rounded-full bg-foreground/70"
        value={isPending || total === 0 ? 0 : (learned / total) * 100}
      />
      <Text className="w-24 text-right text-xs text-muted-foreground tabular-nums">
        {isPending ? "…" : `${learned.toLocaleString()} / ${total.toLocaleString()}`}
      </Text>
    </View>
  );
}
